import { useCallback } from "react";

import { useApiAccessContext } from "@musicfy/libs/ApiAccessProvider";
import { api } from "@musicfy/utils";

import { type TApiKey } from "../IApiAccess";

export const useApiAccess = () => {
  const {
    apiKeys,
    setApiKeys,
    isLoading,
    hasApiAccess,
    apiUsage,
    apiInvoices,
    apiSubscription,
    currentBillingCycle,
  } = useApiAccessContext();

  const createApiKeyMutation = api.apiAccess.createApiKey.useMutation();
  const deleteApiKeyMutation = api.apiAccess.deleteApiKey.useMutation();
  const updateApiKeyLabelMutation =
    api.apiAccess.updateApiKeyLabel.useMutation();

  const createApiKey = useCallback(async (): Promise<TApiKey[]> => {
    if (!apiKeys) {
      return [];
    }
    const createdApiKey: TApiKey = await createApiKeyMutation.mutateAsync();

    const _newApiKeys = [createdApiKey, ...apiKeys];

    setApiKeys(_newApiKeys);

    return _newApiKeys;
  }, [apiKeys, createApiKeyMutation, setApiKeys]);

  const deleteApiKey = useCallback(
    async (apiKeyId: string) => {
      setApiKeys((prevApiKeys) => {
        return prevApiKeys?.filter((apiKey) => apiKey.id !== apiKeyId);
      });

      await deleteApiKeyMutation.mutateAsync({
        id: apiKeyId,
      });
    },
    [deleteApiKeyMutation, setApiKeys]
  );

  const updateApiKeyLabel = useCallback(
    (apiKeyId: string) => {
      if (!apiKeys) return undefined;
      const apiKeyIndex = apiKeys?.findIndex(
        (apiKey) => apiKey.id === apiKeyId
      );

      return (label: string, upload = false) => {
        setApiKeys((prevApiKeys) => {
          const newApiKeys = [...(prevApiKeys || [])];

          const currentApiKey = newApiKeys[apiKeyIndex];

          if (!currentApiKey) return newApiKeys;

          newApiKeys[apiKeyIndex] = {
            ...currentApiKey,
            label,
          };

          return newApiKeys;
        });

        if (upload) {
          updateApiKeyLabelMutation.mutateAsync({
            id: apiKeyId,
            label: label,
          });
        }
      };
    },
    [apiKeys, setApiKeys, updateApiKeyLabelMutation]
  );

  return {
    apiKeys,
    setApiKeys,
    isLoading,
    createApiKey,
    deleteApiKey,
    hasApiAccess,
    apiUsage,
    apiInvoices,
    currentBillingCycle,
    updateApiKeyLabel,
    apiSubscription,
  };
};

export const useApiKeyState = <K extends keyof TApiKey>(
  apiKeyId: string,
  key: K
) => {
  const { apiKeys, updateApiKeyLabel } = useApiAccess();
  const apiKey = apiKeys?.find((k) => k.id === apiKeyId);

  const value = apiKey?.[key] ?? null;
  const setValue = useCallback(
    (newValue: string, upload = false) => {
      const changeLabel = updateApiKeyLabel(apiKeyId);
      if (!changeLabel) return;
      changeLabel?.(newValue, upload);
    },
    [apiKeyId, updateApiKeyLabel]
  );

  return { value, setValue };
};
